import { CarOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { Alert, Button, Descriptions, Skeleton, Tag, Typography } from 'antd';
import dayjs from 'dayjs';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ClearanceState } from '@tms/shared';

import { api } from '@/api/client';
import { DriverDrawer } from '@/pages/drivers/DriverDrawer';

import { SectionHeader } from './SidePanel';

interface DriverSummary {
  licenses: Array<{ id: number; number: string; categories: string[]; expiresAt: string }>;
  currentWaybill: {
    id: number;
    number: string;
    vehicle: { garageNumber: string; plateNumber: string | null } | null;
  } | null;
}

interface Standings {
  limit: number;
  periodMonths: number;
  drivers: Array<{ driverId: number; count: number; blocked: boolean }>;
}

const CLEARANCE_COLOR: Record<ClearanceState, string> = {
  PASSED: 'success',
  CONDITIONAL: 'warning',
  MISSING: 'default',
  EXPIRED: 'warning',
  FAILED: 'error',
};

/**
 * Сводка водительского профиля прямо в карточке сотрудника: можно ли его
 * сейчас выпускать в рейс и почему. Подробности и правка — в карточке водителя.
 */
export function DriverSection({ driverId }: { driverId: number }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  const driver = useQuery({
    queryKey: ['driver', driverId],
    queryFn: async () => (await api.get<DriverSummary>(`/drivers/${driverId}`)).data,
  });
  const eligibility = useQuery({
    queryKey: ['driver-eligibility', driverId],
    queryFn: async () =>
      (await api.get<Array<{ code: string; message: string }>>(`/drivers/${driverId}/eligibility`)).data,
  });
  const medical = useQuery({
    queryKey: ['driver-medical-clearance', driverId],
    queryFn: async () =>
      (
        await api.get<{ state: ClearanceState; label: string; validUntil: string | null }>(
          `/drivers/${driverId}/medical-clearance`,
        )
      ).data,
  });
  const standings = useQuery({
    queryKey: ['violation-standings'],
    queryFn: async () => (await api.get<Standings>('/violations/standings')).data,
  });

  if (driver.isLoading) return <Skeleton active />;

  const license = driver.data?.licenses[0];
  const standing = standings.data?.drivers.find((d) => d.driverId === driverId);
  const issues = eligibility.data ?? [];

  return (
    <>
      <SectionHeader
        title={t('Водитель')}
        extra={
          <Button icon={<CarOutlined />} onClick={() => setOpen(true)}>
            {t('Открыть карточку водителя')}
          </Button>
        }
      />

      {issues.length === 0 ? (
        <Alert type="success" showIcon style={{ marginBottom: 16 }} message={t('Допущен к работе')} />
      ) : (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          message={t('Не допущен к работе')}
          description={
            <ul style={{ margin: 0, paddingLeft: 18 }}>
              {issues.map((issue) => (
                <li key={issue.code}>{issue.message}</li>
              ))}
            </ul>
          }
        />
      )}

      <Descriptions
        bordered
        size="small"
        column={1}
        labelStyle={{ width: 240 }}
        items={[
          {
            key: 'license',
            label: t('Водительское удостоверение'),
            children: license ? (
              <>
                {license.number} · {license.categories.join(', ')} · {t('до')}{' '}
                {dayjs(license.expiresAt).format('DD.MM.YYYY')}
                {dayjs(license.expiresAt).isBefore(dayjs(), 'day') && (
                  <Tag color="error" bordered={false} style={{ marginLeft: 8 }}>
                    {t('просрочено')}
                  </Tag>
                )}
              </>
            ) : (
              <Typography.Text type="danger">{t('не внесено')}</Typography.Text>
            ),
          },
          {
            key: 'medical',
            label: t('Предрейсовый медосмотр'),
            children: medical.data ? (
              <>
                <Tag color={CLEARANCE_COLOR[medical.data.state]} bordered={false}>
                  {t(medical.data.label)}
                </Tag>
                {medical.data.validUntil && (
                  <Typography.Text type="secondary">
                    {t('действует до')} {dayjs(medical.data.validUntil).format('DD.MM.YYYY HH:mm')}
                  </Typography.Text>
                )}
              </>
            ) : (
              '—'
            ),
          },
          {
            key: 'violations',
            label: t('Нарушения за период'),
            children: standings.data ? (
              <>
                <Tag color={standing?.blocked ? 'error' : 'default'} bordered={false}>
                  {standing?.count ?? 0}/{standings.data.limit}
                </Tag>
                <Typography.Text type="secondary">
                  {t('за')} {standings.data.periodMonths} {t('мес.')}
                  {standing?.blocked ? ` · ${t('путевые листы не выдаются')}` : ''}
                </Typography.Text>
              </>
            ) : (
              '—'
            ),
          },
          {
            key: 'waybill',
            label: t('Текущий путевой лист'),
            children: driver.data?.currentWaybill
              ? `${driver.data.currentWaybill.number}${
                  driver.data.currentWaybill.vehicle ? ` · ${driver.data.currentWaybill.vehicle.garageNumber}` : ''
                }`
              : t('нет'),
          },
        ]}
      />

      <DriverDrawer driverId={open ? driverId : null} onClose={() => setOpen(false)} />
    </>
  );
}
